import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Typography, Button, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Switch, Grid, Stack, Chip, Divider, Paper, MenuItem, Select, FormControl, InputLabel, Autocomplete
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PersonOff as ExclusionIcon,
    Save as SaveIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';

const GroupRecurringRules = ({ setIsLoggedIn }) => {
    const tenant = useMemo(() => localStorage.getItem('tenant') || '', []);
    const API_URL = process.env.REACT_APP_API_URL1;

    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);

    // Lookups
    const [elements, setElements] = useState([]);
    const [orgNodes, setOrgNodes] = useState([]);
    const [grades, setGrades] = useState([]);
    const [branches, setBranches] = useState([]);
    const [staffList, setStaffList] = useState([]);

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState('rule'); // 'rule' or 'exclusions'
    const [editingRule, setEditingRule] = useState(null);
    const [formData, setFormData] = useState({});

    // Exclusions state for the selected rule
    const [ruleExclusions, setRuleExclusions] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const config = { withCredentials: true, headers: { 'X-Tenant': tenant } };
            const [rulesRes, elemRes, nodeRes, gradeRes, branchRes, staffRes] = await Promise.all([
                axios.get(`${API_URL}/sm/payroll/recurring-rules`, config),
                axios.get(`${API_URL}/sm/payroll/elements`, config),
                axios.get(`${API_URL}/som/nodes`, config),
                axios.get(`${API_URL}/som/grades`, config),
                axios.get(`${API_URL}/som/branches`, config),
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff`, config)
            ]);

            if (rulesRes.data.success) setRules(rulesRes.data.data);
            if (elemRes.data.success) setElements(elemRes.data.data);
            if (nodeRes.data.success) setOrgNodes(buildHierarchicalList(nodeRes.data.data));
            if (gradeRes.data.success) setGrades(gradeRes.data.data);
            if (branchRes.data.success) setBranches(branchRes.data.data);

            const staffData = Array.isArray(staffRes.data) ? staffRes.data : (staffRes.data?.rows || []);
            setStaffList(staffData);

        } catch (err) {
            console.error("Error fetching rules:", err);
        } finally {
            setLoading(false);
        }
    }, [tenant, API_URL]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenRule = (rule = null) => {
        setDialogType('rule');
        if (rule) {
            setEditingRule(rule);
            setFormData({
                ...rule,
                effective_from: rule.effective_from ? rule.effective_from.split('T')[0] : '',
                effective_to: rule.effective_to ? rule.effective_to.split('T')[0] : ''
            });
        } else {
            setEditingRule(null);
            setFormData({
                rule_name: '',
                element_id: '',
                amount: '',
                currency: 'MMK',
                scope_type: 'SOM',
                org_node_id: '',
                grade_id: '',
                branch_id: '',
                include_descendants: 0,
                priority: 1,
                effective_from: '',
                effective_to: '',
                is_active: 1
            });
        }
        setOpenDialog(true);
    };

    const handleOpenExclusions = async (rule) => {
        setEditingRule(rule);
        setDialogType('exclusions');
        try {
            const config = { withCredentials: true, headers: { 'X-Tenant': tenant } };
            const res = await axios.get(`${API_URL}/sm/payroll/recurring-rules/${rule.id}`, config);
            if (res.data.success) {
                setRuleExclusions(res.data.data.exclusions || []);
            }
        } catch (err) {
            console.error("Error fetching exclusions", err);
        }
        
        setFormData({
            staff_id: null,
            effective_from: '',
            effective_to: '',
            reason: ''
        });
        setOpenDialog(true);
    };

    const handleSaveRule = async () => {
        try {
            const config = { withCredentials: true, headers: { 'X-Tenant': tenant } };
            const payload = { ...formData };
            if (payload.scope_type !== 'SOM') { payload.org_node_id = null; payload.include_descendants = 0; }
            if (payload.scope_type !== 'GRADE') payload.grade_id = null;
            if (payload.scope_type !== 'BRANCH') payload.branch_id = null;

            if (editingRule) {
                await axios.put(`${API_URL}/sm/payroll/recurring-rules/${editingRule.id}`, payload, config);
            } else {
                await axios.post(`${API_URL}/sm/payroll/recurring-rules`, payload, config);
            }
            fetchData();
            setOpenDialog(false);
        } catch (err) {
            console.error("Save failed", err);
            alert("Failed to save rule. Please check all fields.");
        }
    };

    const handleDeleteRule = async (id) => {
        if (!window.confirm('Delete this rule? This will stop generating group payments.')) return;
        try {
            const config = { withCredentials: true, headers: { 'X-Tenant': tenant } };
            await axios.delete(`${API_URL}/sm/payroll/recurring-rules/${id}`, config);
            fetchData();
        } catch (err) {
            console.error("Delete failed", err);
            alert("Delete failed.");
        }
    };

    const handleAddExclusion = async () => {
        if (!formData.staff_id || !formData.effective_from) {
            alert("Staff and Effective From are required");
            return;
        }
        try {
            const config = { withCredentials: true, headers: { 'X-Tenant': tenant } };
            await axios.post(`${API_URL}/sm/payroll/recurring-rules/${editingRule.id}/exclusions`, {
                staff_id: formData.staff_id.id,
                effective_from: formData.effective_from,
                effective_to: formData.effective_to,
                reason: formData.reason
            }, config);
            
            // Refresh exclusions
            const res = await axios.get(`${API_URL}/sm/payroll/recurring-rules/${editingRule.id}`, config);
            if (res.data.success) {
                setRuleExclusions(res.data.data.exclusions || []);
                setFormData({ staff_id: null, effective_from: '', effective_to: '', reason: '' });
            }
        } catch (err) {
            console.error("Failed to add exclusion", err);
            alert("Failed to add exclusion");
        }
    };

    const handleDeleteExclusion = async (exclusionId) => {
        if (!window.confirm("Remove this exclusion?")) return;
        try {
            const config = { withCredentials: true, headers: { 'X-Tenant': tenant } };
            await axios.delete(`${API_URL}/sm/payroll/recurring-rules/exclusions/${exclusionId}`, config);
            // Refresh exclusions
            const res = await axios.get(`${API_URL}/sm/payroll/recurring-rules/${editingRule.id}`, config);
            if (res.data.success) setRuleExclusions(res.data.data.exclusions || []);
        } catch (err) {
            console.error("Failed to delete exclusion", err);
            alert("Failed to delete exclusion");
        }
    };

    const columns = [
        { field: 'rule_name', headerName: 'Rule Name', flex: 1, minWidth: 200 },
        { field: 'element_name', headerName: 'Pay Element', minWidth: 150 },
        { 
            headerName: 'Scope', 
            minWidth: 200, 
            valueGetter: p => {
                if(p.data.scope_type === 'SOM') return `Org: ${p.data.org_node_name || 'N/A'}`;
                if(p.data.scope_type === 'GRADE') return `Grade: ${p.data.grade_name || 'N/A'}`;
                if(p.data.scope_type === 'BRANCH') return `Branch: ${p.data.branch_name || 'N/A'}`;
                return p.data.scope_type;
            }
        },
        { field: 'amount', headerName: 'Amount', minWidth: 120, valueFormatter: p => `${p.value} ${p.data.currency}` },
        { field: 'priority', headerName: 'Priority', width: 100 },
        { field: 'is_active', headerName: 'Status', width: 100, cellRenderer: p => <Chip size="small" color={p.value ? "success" : "default"} label={p.value ? "Active" : "Inactive"} /> },
        {
            headerName: 'Actions',
            width: 150,
            cellRenderer: p => (
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Edit Rule"><IconButton size="small" onClick={() => handleOpenRule(p.data)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Manage Exclusions"><IconButton size="small" color="warning" onClick={() => handleOpenExclusions(p.data)}><ExclusionIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete Rule"><IconButton size="small" color="error" onClick={() => handleDeleteRule(p.data.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>Group Payment Rules</Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>Manage automated recurring allowances/deductions by groups (SOM, Grade, Branch).</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenRule(null)} sx={{ borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                        Create Rule
                    </Button>
                </Stack>

                <Paper sx={{ flex: 1, p: 2, borderRadius: 4 }} className="ag-theme-quartz">
                    <AgGridReact
                        rowData={rules}
                        columnDefs={columns}
                        rowSelection="single"
                        pagination={true}
                        paginationPageSize={20}
                        domLayout="autoHeight"
                    />
                </Paper>
            </Box>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth={dialogType === 'rule' ? "md" : "md"} fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #e2e8f0', p: 3 }}>
                    {dialogType === 'rule' ? (editingRule ? 'Edit Group Rule' : 'Create Group Rule') : `Manage Exclusions: ${editingRule?.rule_name}`}
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {dialogType === 'rule' && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={8}>
                                <TextField fullWidth label="Rule Name" value={formData.rule_name || ''} onChange={e => setFormData({ ...formData, rule_name: e.target.value })} required />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select value={formData.is_active !== undefined ? formData.is_active : 1} label="Status" onChange={e => setFormData({ ...formData, is_active: e.target.value })}>
                                        <MenuItem value={1}>Active</MenuItem>
                                        <MenuItem value={0}>Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth required>
                                    <InputLabel>Pay Element</InputLabel>
                                    <Select value={formData.element_id || ''} label="Pay Element" onChange={e => setFormData({ ...formData, element_id: e.target.value })}>
                                        {elements.map(e => <MenuItem key={e.id} value={e.id}>{e.element_code} - {e.element_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth type="number" label="Amount" value={formData.amount || ''} onChange={e => setFormData({ ...formData, amount: e.target.value })} required />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Priority (Higher = Wins)" type="number" value={formData.priority || 1} onChange={e => setFormData({ ...formData, priority: e.target.value })} />
                            </Grid>

                            <Grid item xs={12}><Divider><Chip label="Target Scope" /></Divider></Grid>

                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Scope Type</InputLabel>
                                    <Select value={formData.scope_type || 'SOM'} label="Scope Type" onChange={e => setFormData({ ...formData, scope_type: e.target.value })}>
                                        <MenuItem value="SOM">Organization Node (SOM)</MenuItem>
                                        <MenuItem value="GRADE">Job Grade</MenuItem>
                                        <MenuItem value="BRANCH">Branch</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={8}>
                                {formData.scope_type === 'SOM' && (
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <FormControl fullWidth>
                                            <InputLabel>Organization Node</InputLabel>
                                            <Select value={formData.org_node_id || ''} label="Organization Node" onChange={e => setFormData({ ...formData, org_node_id: e.target.value })}>
                                                {orgNodes.map(n => <MenuItem key={n.id} value={n.id}>{n.displayName}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                        <Switch checked={formData.include_descendants === 1} onChange={e => setFormData({ ...formData, include_descendants: e.target.checked ? 1 : 0 })} />
                                        <Typography variant="caption" sx={{ minWidth: 100 }}>Include Children</Typography>
                                    </Stack>
                                )}
                                {formData.scope_type === 'GRADE' && (
                                    <FormControl fullWidth>
                                        <InputLabel>Job Grade</InputLabel>
                                        <Select value={formData.grade_id || ''} label="Job Grade" onChange={e => setFormData({ ...formData, grade_id: e.target.value })}>
                                            {grades.map(g => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                )}
                                {formData.scope_type === 'BRANCH' && (
                                    <FormControl fullWidth>
                                        <InputLabel>Branch</InputLabel>
                                        <Select value={formData.branch_id || ''} label="Branch" onChange={e => setFormData({ ...formData, branch_id: e.target.value })}>
                                            {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>

                            <Grid item xs={12}><Divider><Chip label="Validity Period" /></Divider></Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth type="date" label="Effective From" InputLabelProps={{ shrink: true }} value={formData.effective_from || ''} onChange={e => setFormData({ ...formData, effective_from: e.target.value })} required />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth type="date" label="Effective To (Optional)" InputLabelProps={{ shrink: true }} value={formData.effective_to || ''} onChange={e => setFormData({ ...formData, effective_to: e.target.value })} />
                            </Grid>

                        </Grid>
                    )}

                    {dialogType === 'exclusions' && (
                        <Box>
                            <Paper sx={{ p: 2, bgcolor: '#f8fafc', mb: 3, border: '1px solid #e2e8f0' }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>Add New Exclusion</Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <Autocomplete
                                            options={staffList}
                                            getOptionLabel={(option) => `${option.employee_number} - ${option.full_name}`}
                                            value={formData.staff_id || null}
                                            onChange={(e, val) => setFormData({ ...formData, staff_id: val })}
                                            renderInput={(params) => <TextField {...params} label="Select Staff" size="small" />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField fullWidth type="date" label="From" size="small" InputLabelProps={{ shrink: true }} value={formData.effective_from || ''} onChange={e => setFormData({ ...formData, effective_from: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField fullWidth type="date" label="To" size="small" InputLabelProps={{ shrink: true }} value={formData.effective_to || ''} onChange={e => setFormData({ ...formData, effective_to: e.target.value })} />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Button variant="contained" fullWidth onClick={handleAddExclusion} sx={{ textTransform: 'none', fontWeight: 700 }}>Add</Button>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>Active Exclusions ({ruleExclusions.length})</Typography>
                            {ruleExclusions.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>No exclusions configured for this rule.</Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {ruleExclusions.map(ex => (
                                        <Paper key={ex.id} sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e2e8f0' }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{ex.employee_number} - {ex.full_name}</Typography>
                                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                    Excluded from {new Date(ex.effective_from).toLocaleDateString()}
                                                    {ex.effective_to && ` to ${new Date(ex.effective_to).toLocaleDateString()}`}
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteExclusion(ex.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Button startIcon={<CloseIcon />} onClick={() => setOpenDialog(false)} sx={{ color: '#64748b', fontWeight: 700, textTransform: 'none' }}>Close</Button>
                    {dialogType === 'rule' && (
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveRule} sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>Save Rule</Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GroupRecurringRules;
